/**
 * Timeline selection — a global signal holding the set of currently-selected
 * keyframes (value points on cap lanes + color markers on color lanes).
 *
 * Model: a Set of "lane:step" keys. Cross-lane selection is legal — a
 * marquee that crosses R/G/B lanes selects value points on all three.
 *
 * Clipboard: paired with selection; not persisted. `navigator.clipboard`
 * handles cross-tab / post-reload paste at the transport layer; this
 * module owns the in-editor representation.
 */

import { createSignal, type Accessor } from 'solid-js';

/** Stable key for a keyframe reference: `${laneId}:${stepId}`. */
export type SelectionKey = string;

export function selKey(laneId: string, stepId: string): SelectionKey {
  return `${laneId}:${stepId}`;
}
export function parseSelKey(k: SelectionKey): { laneId: string; stepId: string } {
  const ix = k.indexOf(':');
  return { laneId: k.slice(0, ix), stepId: k.slice(ix + 1) };
}

const [selectionSignal, setSelectionSignal] = createSignal<Set<SelectionKey>>(new Set());

export const selection: Accessor<Set<SelectionKey>> = selectionSignal;

export function isSelected(laneId: string, stepId: string): boolean {
  return selectionSignal().has(selKey(laneId, stepId));
}

/** Replace the entire selection. */
export function setSelection(keys: Iterable<SelectionKey>): void {
  setSelectionSignal(new Set<SelectionKey>(keys));
}

/** Clear all selection. */
export function clearSelection(): void {
  setSelectionSignal(new Set<SelectionKey>());
}

/** Add one keyframe to the selection (Shift-click, marquee-add). */
export function addToSelection(laneId: string, stepId: string): void {
  const k = selKey(laneId, stepId);
  const next = new Set<SelectionKey>(selectionSignal());
  next.add(k);
  setSelectionSignal(next);
}

/** Toggle a keyframe in the selection (Cmd/Ctrl-click). */
export function toggleInSelection(laneId: string, stepId: string): void {
  const k = selKey(laneId, stepId);
  const next = new Set<SelectionKey>(selectionSignal());
  if (next.has(k)) next.delete(k);
  else next.add(k);
  setSelectionSignal(next);
}

/** Remove selection entries whose lane or step no longer exists. Called
 *  after mutations that could invalidate references. */
export function pruneSelection(validKeys: Set<SelectionKey>): void {
  const cur = selectionSignal();
  let mutated = false;
  const next = new Set<SelectionKey>();
  for (const k of cur) {
    if (validKeys.has(k)) next.add(k);
    else mutated = true;
  }
  if (mutated) setSelectionSignal(next);
}

// ─── Clipboard ────────────────────────────────────────────────────
//
// Serialized shape written to `navigator.clipboard` as JSON (fallback:
// in-memory). Anchor = earliest selected step's bar position; paste
// re-anchors the group at the playhead. Preserving `capability` lets a
// cross-fixture paste route entries to matching R/G/B channels on the
// destination.

export const CLIPBOARD_MIME = 'application/x-opbuttons-timeline-clipboard';
/** v1 — automation only (valuePoint/colorMarker), offset-from-earliest anchor.
 *  v2 — all step kinds, delta-from-playhead anchoring.
 *  v3 — all step kinds, delta-from-LEFTMOST anchoring (leftmost lands
 *       at playhead on paste). More predictable when the copy playhead
 *       is far from the copied cluster. Field kept named
 *       `offsetFromLeftmostBars` so the semantics are self-documenting.
 *  Reader translates older versions on the fly. */
export const CLIPBOARD_VERSION = 3;

export interface ClipboardEntry {
  /** Bars offset from the leftmost selected step's position.
   *  Non-negative by construction (leftmost = 0). Paste computes
   *  `stepPos = playhead_at_paste + offsetFromLeftmostBars`, so the
   *  leftmost lands at the playhead and everything else preserves its
   *  spacing relative to the leftmost. */
  offsetFromLeftmostBars: number;
  /** Source lane id. Paste tries to route back to this lane first; if
   *  it no longer exists in the destination timeline, falls back to
   *  laneKind matching. Empty string when unknown (v1 legacy clipboard
   *  payloads carry no lane id). */
  sourceLaneId: string;
  /** Source lane kind — routing fallback when the source lane is gone.
   *  Undefined when the source lane had no laneKind set (legacy lanes). */
  sourceLaneKind: string | undefined;
  /** RETIRED 2026-08-04 (fixture-cap sub-lanes are gone) — kept optional
   *  + read-tolerated so old clipboard payloads don't error, never
   *  written by current code. */
  capability?: string;
  /** Step payload minus the id (fresh id assigned on paste). Keeps the
   *  original `kind` so paste can validate lane compatibility. */
  step: Record<string, unknown>;
}

export interface ClipboardPayload {
  version: number;
  /** Total span of the copied selection in bars (last position - first).
   *  Used by Duplicate to offset the clone selection cleanly after the
   *  original. */
  spanBars: number;
  entries: ClipboardEntry[];
}

let inMemoryClipboard: ClipboardPayload | null = null;

// Reactive "have I copied anything this session?" flag. Only reflects our
// own copy/cut operations — the system clipboard could hold something from
// another app, but we can't check that synchronously without a permission
// prompt. Menus grey out Paste when this is false.
const [hasClipboardSignal, setHasClipboardSignal] = createSignal(false);
export const hasClipboard: Accessor<boolean> = hasClipboardSignal;

// ─── Marquee range (shared, x-axis) ───────────────────────────────
//
// The marquee rectangle itself lives inside the LaneTrack that started
// it, but its horizontal extent (in fractional bars) is useful to
// consumers outside the LaneTrack — specifically the Ruler, which wants
// to highlight bar numbers whose bar overlaps the marquee. We publish
// just the fractional-bar range while a marquee is active.

const [marqueeBarRangeSignal, setMarqueeBarRangeSignal] = createSignal<
  { fromBar: number; toBar: number } | null
>(null);
export const marqueeBarRange: Accessor<{ fromBar: number; toBar: number } | null> =
  marqueeBarRangeSignal;
export function setMarqueeBarRange(r: { fromBar: number; toBar: number } | null): void {
  setMarqueeBarRangeSignal(r);
}

export function getInMemoryClipboard(): ClipboardPayload | null {
  return inMemoryClipboard;
}
export function setInMemoryClipboard(payload: ClipboardPayload | null): void {
  inMemoryClipboard = payload;
  setHasClipboardSignal(payload !== null);
}

/** Write to the system clipboard as JSON with our MIME tag inside the
 *  payload (browsers can only expose text/html to `navigator.clipboard.write`
 *  reliably without user permission prompts; we tunnel through text/plain
 *  and parse on paste). Also stores in-memory as a fallback. */
export async function writeClipboard(payload: ClipboardPayload): Promise<void> {
  setInMemoryClipboard(payload);
  if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
  const wrapped = { mime: CLIPBOARD_MIME, payload };
  try {
    await navigator.clipboard.writeText(JSON.stringify(wrapped));
  } catch {
    // Clipboard write can fail if the document isn't focused; in-memory
    // fallback keeps paste working within the current session.
  }
}

export async function readClipboard(): Promise<ClipboardPayload | null> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.readText) {
    try {
      const raw = await navigator.clipboard.readText();
      const parsed = JSON.parse(raw);
      if (parsed && parsed.mime === CLIPBOARD_MIME) {
        const p = parsed.payload;
        if (p?.version === CLIPBOARD_VERSION) return p as ClipboardPayload;
        // v1 → v3 upgrade: v1 stored `offsetBars` relative to earliest
        // selected step, which is exactly the v3 semantic under a
        // different name.
        if (p?.version === 1 && Array.isArray(p.entries)) {
          const upgraded: ClipboardPayload = {
            version: CLIPBOARD_VERSION,
            spanBars: p.spanBars ?? 0,
            entries: p.entries.map((e: Record<string, unknown>) => ({
              offsetFromLeftmostBars: (e.offsetBars as number) ?? 0,
              sourceLaneId: '',
              sourceLaneKind: (e.laneKind as string) ?? 'mixed',
              capability: e.capability as string | undefined,
              step: (e.step as Record<string, unknown>) ?? {},
            })),
          };
          return upgraded;
        }
        // v2 → v3 upgrade: v2 stored deltas from the copy-time playhead
        // (any float, incl. negative). Re-anchor to leftmost = the entry
        // with the smallest delta becomes 0, everything else shifts up.
        if (p?.version === 2 && Array.isArray(p.entries)) {
          let minDelta = Infinity;
          for (const e of p.entries) {
            const d = (e.deltaFromPlayheadBars as number) ?? 0;
            if (d < minDelta) minDelta = d;
          }
          if (!isFinite(minDelta)) minDelta = 0;
          const upgraded: ClipboardPayload = {
            version: CLIPBOARD_VERSION,
            spanBars: p.spanBars ?? 0,
            entries: p.entries.map((e: Record<string, unknown>) => ({
              offsetFromLeftmostBars: ((e.deltaFromPlayheadBars as number) ?? 0) - minDelta,
              sourceLaneId: (e.sourceLaneId as string) ?? '',
              sourceLaneKind: e.sourceLaneKind as string | undefined,
              capability: e.capability as string | undefined,
              step: (e.step as Record<string, unknown>) ?? {},
            })),
          };
          return upgraded;
        }
      }
    } catch {
      // fall through to in-memory
    }
  }
  return inMemoryClipboard;
}
